import { Button, InputNumber, Select, Typography } from 'antd'
import { UilTrashAlt } from '@iconscout/react-unicons'
import { PlusOutlined } from '@ant-design/icons'
import Form from '$components/Form'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
dayjs.extend(utc)

import { CheckCircle, ExclamationCircle, TimesCircle } from '$components/Icons'
import Tooltip from '$components/Tooltip'

const { Text } = Typography

const createColumnsConfig = (
  t,
  orderPartId,
  formData,
  invalidProviderOffers,
  addProviderOffer,
  deleteProviderOffer,
  providerOffersStored,
  isBatchMode = false,
  providerOffers = [],
) => {
  const columns = [
    {
      title: (
        <Tooltip placement="topLeft" title={t('received_requests.amount')}>
          {t('received_requests.amount')}
        </Tooltip>
      ),
      key: 'amount',
      width: 90,
      render: (record) => {
        return (
          <>
            <Form.Item
              name={[orderPartId, 'provider_offers', record.offer_id, 'amount']}
              hidden={!record.editable && !isBatchMode}
              style={{ margin: 0 }}
              rules={[{ required: true, type: 'number', min: 1 }]}
            >
              <InputNumber min={1} size="small" style={{ width: '54px' }} />
            </Form.Item>
            {!record.editable && !isBatchMode ? record.amount : null}
          </>
        )
      },
    },
    ...(isBatchMode
      ? [
          {
            title: t('Price per unit'),
            key: 'price',
            width: 100,
            render: (record) => {
              const price =
                formData?.provider_offers?.[record.offer_id]?.base_price
              return price ? (
                <Text strong style={{ fontSize: '12px' }}>
                  {(price / 100).toFixed(2)} €
                </Text>
              ) : (
                <Text type="secondary">-</Text>
              )
            },
          },
          {
            title: t('received_requests.rebate_info'),
            key: 'rebate',
            width: 100,
            render: (record, _index) => {
              const offersWithAmounts = providerOffers.filter(
                (offer) => offer.amount,
              )

              if (offersWithAmounts.length === 0) {
                return <Text type="secondary">-</Text>
              }

              const baselineOffer = offersWithAmounts.reduce((min, offer) =>
                !min.amount || offer.amount < min.amount ? offer : min,
              )

              if (baselineOffer.offer_id === record.offer_id) {
                return <Text type="secondary">-</Text>
              }

              const baselinePrice =
                formData?.provider_offers?.[baselineOffer.offer_id]?.base_price
              const currentPrice =
                formData?.provider_offers?.[record.offer_id]?.base_price

              if (baselinePrice && currentPrice) {
                const rebate = Math.round(
                  ((baselinePrice - currentPrice) / baselinePrice) * 100,
                )
                return (
                  <Text
                    type={rebate > 0 ? 'success' : 'danger'}
                    style={{ fontSize: '11px' }}
                  >
                    {rebate > 0 ? '-' : '+'}
                    {Math.abs(rebate)}%
                  </Text>
                )
              }
              return <Text type="secondary">-</Text>
            },
          },
        ]
      : []),
    ...(providerOffersStored
      ? []
      : [
          {
            title: (
              <Tooltip
                placement="topLeft"
                title={t('received_requests.desired_delivery_date')}
              >
                {t('received_requests.desired_delivery_date')}
              </Tooltip>
            ),
            // dataIndex: 'desired_delivery_date',
            key: 'desired_delivery_date',
            render: (record) => {
              const deliveryDateOptions =
                providerOffers
                  ?.filter(
                    (offer) =>
                      offer.editable !== true && offer.desired_delivery_date,
                  )
                  ?.map((offer) => ({
                    value: offer.desired_delivery_date,
                    label: dayjs(offer.desired_delivery_date).format(
                      'DD/MM/YYYY',
                    ),
                  })) || []

              return (
                <Select
                  size="small"
                  defaultValue={
                    !record.editable && record.desired_delivery_date
                  }
                  options={deliveryDateOptions}
                  style={{ width: '120px' }}
                />
              )
            },
          },
        ]),
    {
      title: t('received_requests.status'),
      render: (record) => {
        const providerOffersFormData = formData?.provider_offers?.[record.key]

        let allPriceRulesValid = true
        let allProductionTimeRulesValid = true

        if (
          providerOffersFormData?.price_rules &&
          Object.keys(providerOffersFormData.price_rules).length > 0
        ) {
          allPriceRulesValid = Object.values(
            providerOffersFormData.price_rules,
          ).every(
            (rule) =>
              rule.min_parts && rule.discount_amount && rule.discount_unit,
          )
        }

        if (
          providerOffersFormData?.production_time_rules &&
          Object.keys(providerOffersFormData.production_time_rules).length > 0
        ) {
          allProductionTimeRulesValid = Object.values(
            providerOffersFormData.production_time_rules,
          ).every(
            (rule) =>
              rule.max_days && rule.surcharge_amount && rule.surcharge_unit,
          )
        }

        return providerOffersFormData?.base_price &&
          providerOffersFormData?.base_production_time &&
          allPriceRulesValid &&
          allProductionTimeRulesValid ? (
          <CheckCircle />
        ) : (
          <Tooltip title={t('received_requests.specify_offer')}>
            {invalidProviderOffers.includes(record.key) ? (
              <TimesCircle />
            ) : (
              <ExclamationCircle />
            )}
          </Tooltip>
        )
      },
    },
    {
      title: (
        <Button size="small" onClick={addProviderOffer}>
          <PlusOutlined />
        </Button>
      ),
      render: (record) =>
        providerOffers.length > 1 &&
        record.editable && (
          <Button
            size="small"
            type="secondary"
            onClick={(event) => {
              event.stopPropagation()
              deleteProviderOffer(record.offer_id)
            }}
          >
            <UilTrashAlt size="17" color="#495057" />
          </Button>
        ),
    },
  ]

  return columns
}

export default createColumnsConfig
